import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { useEinsteinChat, parseEinsteinRoute } from '../hooks/useEinsteinChat';
import { listEinsteinConversations } from '../api';
import EinsteinMessage from '../components/EinsteinMessage';
import PageChrome from '../components/PageChrome';

// Dedicated Einstein page (2026-09-03) — a full-page, Claude-like single-conversation chat,
// reached via the "Einstein" nav pill (App.js/PageChrome.js). Distinct from the compact console
// embedded in Home's lower half (EinsteinConsole.js): that one stays for quick questions at a
// glance without leaving Home; this one is the dedicated deep-conversation surface. Both share
// the same state machine and response-shape rendering (useEinsteinChat.js/EinsteinMessage.js) —
// only the layout differs.
//
// Conversation-history sidebar (2026-09-10) — now that einstein_conversations/einstein_messages
// persistence actually exists (see EINSTEIN_ROADMAP.md), lists every past conversation (across
// both this tab and Home's compact console — one shared history per trainer) and loads one back
// into the same thread shape a live turn produces.
export default function EinsteinChat({ user, onNavigate }) {
  const { theme } = useTheme();
  const isDay = theme === 'day';
  const accent = isDay ? '#0891b2' : '#22d3ee';
  const textColor = isDay ? '#1c2024' : '#fff';
  const mutedColor = isDay ? 'rgba(20,24,28,.55)' : 'rgba(255,255,255,.55)';
  const einsteinBubbleBg = isDay ? 'rgba(20,24,28,.05)' : 'rgba(255,255,255,.06)';
  const coachBubbleBg = accent;
  const sidebarBg = isDay ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.04)';
  const sidebarBorder = isDay ? 'rgba(24,40,56,.08)' : 'rgba(255,255,255,.08)';
  const rowHoverBg = isDay ? 'rgba(24,40,56,.05)' : 'rgba(255,255,255,.06)';

  // page/gpsTab drive which component App.js renders (its own React state, not URL-reactive
  // after mount — see App.js's handlePageChange comment), so this has to go through
  // onNavigate/handlePageChange like every other nav action, not a raw react-router navigate()
  // (see Home.js's identical goToRoute for the full explanation — same 2026-09-03 bug fix).
  const goToRoute = route => {
    const { page, gpsTab, params } = parseEinsteinRoute(route);
    onNavigate(page, gpsTab || undefined, params);
  };

  const {
    thread, draft, setDraft, status, suggestions, hasSessionToken, hasChatted, send, onOptionClick,
    conversationId, startNewConversation, loadConversation, deleteConversation,
  } = useEinsteinChat({ user });

  const [conversations, setConversations] = useState([]);

  const refreshConversations = useCallback(() => {
    if (!user?.sessionToken) return;
    listEinsteinConversations(user.sessionToken)
      .then(r => setConversations(r?.conversations || []))
      .catch(() => {});
  }, [user?.sessionToken]);

  useEffect(() => { refreshConversations(); }, [refreshConversations]);
  // A new conversation gets created (or an existing one's updated_at bumped) server-side as a
  // side effect of send() — conversationId changing is the signal that the list is now stale.
  useEffect(() => { refreshConversations(); }, [conversationId, refreshConversations]);

  // Delete a conversation from the sidebar (2026-09-10) — window.confirm matches every other
  // destructive action in this app (Medical.js, GPSUpload.js, LineupBuilder.js all use the same
  // native confirm rather than a custom modal). deleteConversation() itself handles falling back
  // to a fresh thread if the deleted conversation was the one on screen; this just re-fetches the
  // list afterward so the deleted row actually disappears.
  const handleDeleteConversation = (e, id, title) => {
    e.stopPropagation(); // don't also trigger the row's own onClick (loadConversation)
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    deleteConversation(id).then(refreshConversations);
  };

  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread, status]);

  return (
    <PageChrome onNavigate={onNavigate} user={user} contentMaxWidth={1080} padding="16px 32px 24px">
      <div style={{ marginTop: 12, height: 'calc(100vh - 110px)', minHeight: 480, display: 'flex', gap: 16 }}>
        {/* History sidebar (2026-09-10) — Claude-like conversation list. */}
        <div style={{
          flex: 'none', width: 232, display: 'flex', flexDirection: 'column', gap: 4,
          background: sidebarBg, border: `1px solid ${sidebarBorder}`, borderRadius: 16,
          padding: 10, overflow: 'hidden',
        }}>
          <button type="button" onClick={startNewConversation}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10,
              border: `1px solid ${accent}`, background: 'transparent', color: accent,
              fontSize: 13.5, fontWeight: 600, cursor: 'pointer', flex: 'none', marginBottom: 6,
            }}>
            <Plus size={15} /> New chat
          </button>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!conversations.length && (
              <div style={{ fontSize: 12.5, color: mutedColor, padding: '8px 10px' }}>No past conversations yet.</div>
            )}
            {conversations.map(c => (
              <div key={c.id} role="button" tabIndex={0} onClick={() => loadConversation(c.id)}
                onKeyDown={e => { if (e.key === 'Enter') loadConversation(c.id); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '9px 6px 9px 10px', borderRadius: 8,
                  background: c.id === conversationId ? rowHoverBg : 'transparent', cursor: 'pointer',
                }}>
                <span style={{
                  flex: 1, minWidth: 0, color: textColor, fontSize: 13,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {c.title}
                </span>
                <button type="button" onClick={e => handleDeleteConversation(e, c.id, c.title)}
                  title="Delete conversation"
                  style={{
                    flex: 'none', padding: 4, borderRadius: 6, border: 'none', background: 'transparent',
                    color: mutedColor, cursor: 'pointer', display: 'flex', alignItems: 'center',
                  }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Fixed-height column, same technique as Home's arc+console (2026-09-03 fix) — the
            message list is what scrolls (flex:1, minHeight:0, overflowY:auto below), never the
            page, so the composer is always on screen without scrolling to reach it. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px 16px', borderBottom: `1px solid ${isDay ? 'rgba(24,40,56,.08)' : 'rgba(255,255,255,.08)'}`, flex: 'none' }}>
          <img src={`${process.env.PUBLIC_URL}/${encodeURIComponent('Einstein PF.png')}`} alt="Einstein"
            style={{ width: 34, height: 34, borderRadius: '999px', flex: 'none', objectFit: 'cover' }} />
          <span style={{ fontFamily: "'Archivo Black','Arial Black',Impact,sans-serif", fontSize: 18, letterSpacing: '.03em', color: textColor }}>EINSTEIN</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: accent }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: accent }} />
            {status === 'thinking' ? 'thinking' : 'ready'}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: mutedColor }}>Your squad analyst</span>
        </div>

        <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 4px' }}>
          {thread.map(msg => (
            <div key={msg.id} style={{
              flex: 'none', maxWidth: '78%', alignSelf: msg.role === 'coach' ? 'flex-end' : 'flex-start',
              background: msg.role === 'coach' ? coachBubbleBg : einsteinBubbleBg,
              color: msg.role === 'coach' ? '#fff' : textColor,
              borderRadius: msg.role === 'coach' ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
              padding: '14px 18px', fontSize: 15.5, lineHeight: 1.55,
            }}>
              {msg.role === 'coach'
                ? msg.text
                : <EinsteinMessage msg={msg} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} onOptionClick={onOptionClick} />}
            </div>
          ))}
          {status === 'thinking' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
              {[0, 0.2, 0.4].map(delay => (
                <span key={delay} style={{ width: 6, height: 6, borderRadius: 999, background: accent, opacity: 0.6 }} />
              ))}
              <span style={{ fontSize: 12.5, color: mutedColor }}>Einstein is checking the registry and squad data…</span>
            </div>
          )}
        </div>

        {!hasChatted && !!suggestions.length && (
          <div style={{ display: 'flex', gap: 10, padding: '0 4px 12px', flexWrap: 'wrap', flex: 'none' }}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => send(s.question)}
                style={{ padding: '9px 16px', borderRadius: 999, background: isDay ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.06)', boxShadow: `inset 0 0 0 1px ${isDay ? 'rgba(226,232,237,.9)' : 'rgba(255,255,255,.12)'}`, fontSize: 13.5, color: mutedColor, cursor: 'pointer', border: 'none' }}>
                {s.question}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px', borderTop: `1px solid ${isDay ? 'rgba(24,40,56,.08)' : 'rgba(255,255,255,.08)'}`, flex: 'none' }}>
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            disabled={!hasSessionToken}
            placeholder={hasSessionToken ? 'Message Einstein…' : 'Sign out and back in to enable Einstein chat'}
            style={{
              flex: 1, padding: '15px 20px', borderRadius: 999, border: 'none', outline: 'none',
              background: isDay ? '#fff' : 'rgba(255,255,255,.07)', color: textColor, fontSize: 15.5,
              boxShadow: `inset 0 0 0 1px ${isDay ? 'rgba(226,232,237,.9)' : 'rgba(255,255,255,.1)'}`,
              opacity: hasSessionToken ? 1 : 0.6,
            }}
          />
          <button type="button" onClick={() => send()} disabled={!hasSessionToken || !draft.trim() || status === 'thinking'}
            style={{
              width: 46, height: 46, borderRadius: 999, border: 'none', flex: 'none',
              background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: hasSessionToken && draft.trim() && status !== 'thinking' ? 'pointer' : 'default',
              opacity: hasSessionToken && draft.trim() && status !== 'thinking' ? 1 : 0.5,
            }}>
            <ArrowUp size={19} />
          </button>
        </div>
        </div>
      </div>
    </PageChrome>
  );
}
